'use client';
import { useMemo, useState } from 'react';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import PersonOffIcon from '@mui/icons-material/PersonOff';
import {
  Avatar,
  Box,
  CircularProgress,
  Divider,
  FormControl,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  Typography,
} from '@mui/material';

import { useT } from '@/hooks';
import { useMarketHolders } from '@/services/query/marketdetails.queries';
import { EventMarket, EventResponse, MarketHolder } from '@/types';
import { formatNumber } from '@/utils';

import { PAGINATION } from '@/constants';

interface TopHoldersTabProps {
  event: EventResponse;
}

const EmptyHoldersBox = () => {
  const { t } = useT();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 6,
        px: 2,
        borderRadius: 3,
        bgcolor: 'background.subtle',
        border: '1px dashed',
        borderColor: 'divider',
      }}
    >
      <Box
        sx={{
          width: 48,
          height: 48,
          borderRadius: '50%',
          bgcolor: 'action.hover',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 1.5,
        }}
      >
        <PersonOffIcon sx={{ fontSize: 24, color: 'text.secondary', opacity: 0.6 }} />
      </Box>
      <Typography variant="body2" sx={{ fontWeight: 600, color: 'text.secondary' }}>
        {t('market.noHoldersFound')}
      </Typography>
      <Typography variant="caption" sx={{ color: 'text.disabled', textAlign: 'center', mt: 0.5 }}>
        {t('market.noHoldersFoundDesc')}
      </Typography>
    </Box>
  );
};

const GlobalEmptyState = () => {
  const { t } = useT();
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        py: 10,
        px: 3,
        textAlign: 'center',
      }}
    >
      <Box
        sx={{
          width: 64,
          height: 64,
          borderRadius: '50%',
          bgcolor: 'error.soft',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2.5,
          opacity: 0.8,
        }}
      >
        <ErrorOutlineIcon sx={{ fontSize: 32, color: 'text.secondary' }} />
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
        {t('market.noMarketData')}
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 300 }}>
        {t('market.noMarketDataDesc')}
      </Typography>
    </Box>
  );
};

const HolderRow = ({ holder, color }: { holder: MarketHolder; color: string }) => {
  const avatarContent = !holder.profileImage
    ? holder.name?.[0] || holder.pseudonym?.[0] || '?'
    : null;
  const formattedAmount = formatNumber(holder.amount);
  const displayName = holder.name || holder.pseudonym || 'Anonymous';

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        py: 1.5,
        borderBottom: '1px solid',
        borderColor: 'divider',
        '&:last-child': { borderBottom: 'none' },
        width: '100%',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1, mr: 2 }}>
        <Avatar
          src={holder.profileImage}
          sx={{
            width: 32,
            height: 32,
            background: (theme) =>
              `linear-gradient(45deg, ${theme.palette.primary.light}, ${theme.palette.primary.main})`,
            flexShrink: 0,
          }}
        >
          {avatarContent}
        </Avatar>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0, flex: 1 }}>
          <Typography
            variant="body2"
            sx={{
              fontWeight: 600,
              color: 'text.primary',
              width: 100,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {displayName}
          </Typography>
          {holder.verified && (
            <CheckCircleIcon sx={{ fontSize: 16, color: 'primary.main', flexShrink: 0 }} />
          )}
        </Box>
      </Box>
      <Typography
        variant="body2"
        sx={{ fontWeight: 700, color, flexShrink: 0, textAlign: 'right' }}
      >
        {formattedAmount}
      </Typography>
    </Box>
  );
};

const HoldersColumn = ({
  title,
  holders,
  color,
}: {
  title: string;
  holders: MarketHolder[];
  color: string;
}) => {
  const { t } = useT();
  const renderHolder = (holder: MarketHolder, idx: number) => (
    <HolderRow key={`${holder.proxyWallet}-${idx}`} holder={holder} color={color} />
  );

  const holdersContent = holders.length > 0 ? holders.map(renderHolder) : <EmptyHoldersBox />;

  return (
    <Box sx={{ flex: 1, minWidth: 0 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1, px: 0.5, width: '100%' }}>
        <Typography
          variant="subtitle2"
          sx={{ fontWeight: 700, color: 'text.primary', whiteSpace: 'nowrap' }}
        >
          {title}
        </Typography>
        <Typography
          variant="caption"
          sx={{ fontWeight: 700, color: 'text.secondary', letterSpacing: 1, ml: 2 }}
        >
          {t('market.shares')}
        </Typography>
      </Box>
      <Divider sx={{ mb: 1 }} />
      <Box sx={{ width: '100%' }}>{holdersContent}</Box>
    </Box>
  );
};

interface HoldersGridProps {
  isLoading: boolean;
  error: any;
  yesHolders: MarketHolder[];
  noHolders: MarketHolder[];
}

const HoldersGrid = ({ isLoading, error, yesHolders, noHolders }: HoldersGridProps) => {
  const { t } = useT();
  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress size={40} />
      </Box>
    );
  }

  if (error) {
    return (
      <Typography color="error" sx={{ textAlign: 'center', py: 4 }}>
        Failed to load holders data.
      </Typography>
    );
  }

  return (
    <Stack
      direction={{ xs: 'column', md: 'row' }}
      spacing={3}
      divider={
        <Divider orientation="vertical" flexItem sx={{ display: { xs: 'none', md: 'block' } }} />
      }
      sx={{ width: '100%' }}
    >
      <HoldersColumn title={t('market.yesHolders')} holders={yesHolders} color="success.main" />
      <HoldersColumn title={t('market.noHolders')} holders={noHolders} color="error.main" />
    </Stack>
  );
};

export const TopHoldersTab = ({ event }: TopHoldersTabProps) => {
  const { t } = useT();
  const markets = event.markets || [];
  const [selectedMarketId, setSelectedMarketId] = useState(markets[0]?.conditionId || '');

  const {
    data: holdersResponse,
    isLoading,
    error,
  } = useMarketHolders({
    market: selectedMarketId,
    limit: PAGINATION.DEFAULT_HOLDERS_LIMIT,
  });

  const handleMarketChange = (e: SelectChangeEvent) => {
    setSelectedMarketId(e.target.value);
  };

  const { yesHolders, noHolders } = useMemo(() => {
    const data = holdersResponse?.data || [];
    const yesData = data.find((d) => d.holders.some((h) => h.outcomeIndex === 0)) || data[0];
    const noData = data.find((d) => d.holders.some((h) => h.outcomeIndex === 1)) || data[1];

    return {
      yesHolders: yesData?.holders || [],
      noHolders: noData?.holders || [],
    };
  }, [holdersResponse]);

  const renderMarketOption = (market: EventMarket) => (
    <MenuItem key={market.conditionId} value={market.conditionId}>
      {market.groupItemTitle || market.question}
    </MenuItem>
  );

  const marketOptions = markets.map(renderMarketOption);

  if (markets.length === 0) {
    return <GlobalEmptyState />;
  }

  return (
    <Box sx={{ pt: 1, maxWidth: { xs: 330, md: '100%' } }}>
      <FormControl size="small" sx={{ mb: 4, minWidth: 200, maxWidth: '100%' }}>
        <Select
          value={selectedMarketId}
          onChange={handleMarketChange}
          displayEmpty
          sx={{
            borderRadius: 2,
            bgcolor: 'background.paper',
            '& .MuiSelect-select': { py: 1, fontWeight: 600 },
          }}
        >
          {marketOptions}
        </Select>
      </FormControl>

      <HoldersGrid
        isLoading={isLoading}
        error={error}
        yesHolders={yesHolders}
        noHolders={noHolders}
      />
    </Box>
  );
};
